import React from "react";
import { Box, IconButton, Typography, Button } from "@mui/material";
import { Close } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import BackdropOverlay from "./BackdropOverlay.jsx";
import {
    LOBBY_EXIT_BUTTON_SX,
    DIALOG_CANCEL_BUTTON_SX,
    DIALOG_PAPER_RADIUS,
} from "./dialogActionButtonSx";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";

const MOBILE_MQ = `@media ${MOBILE_NAV_MEDIA}`;

const DIALOG_FADE_IN = {
    '0%': { opacity: 0 },
    '100%': { opacity: 1 },
};

export default function ConfirmDialog({
    open,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    onConfirm,
    onCancel,
}) {
    if (!open) return null;

    const handleCancel = () => {
        onCancel();
    };

    return (
        <BackdropOverlay open>
            <Box
                role="dialog"
                sx={{
                    "@keyframes dialogFadeIn": { ...DIALOG_FADE_IN },
                    position: 'relative',
                    width: { xs: '100%', sm: '25.5rem', xl: '26.5rem' },
                    maxWidth: '38.5rem',
                    background: `
                        linear-gradient(
                            165deg,
                            rgba(18,14,14,0.98) 0%,
                            rgba(10,10,12,0.99) 48%,
                            rgba(8,8,10,1) 100%
                        )
                    `,
                    borderRadius: DIALOG_PAPER_RADIUS,
                    border: `1px solid ${colors.border.default}`,
                    boxShadow: '0 28px 64px rgba(0, 0, 0, 0.55)',
                    animation: 'dialogFadeIn 0.2s ease-in-out',
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "12%",
                        right: "12%",
                        height: "1px",
                        background: `linear-gradient(90deg, transparent, ${alpha(colors.accent.main, 0.55)}, transparent)`,
                        pointerEvents: "none",
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        px: { xs: '1.35rem', sm: '1.6rem' },
                        pt: '1.55rem',
                        pb: '1.4rem',
                    }}
                >
                    <IconButton
                        onClick={handleCancel}
                        aria-label="Close dialog"
                        sx={{
                            position: 'absolute',
                            top: '0.8rem',
                            right: '0.8rem',
                            width: '1.9rem',
                            height: '1.9rem',
                            borderRadius: '0.55rem',
                            color: colors.text.secondary,
                            backgroundColor: 'rgba(255, 255, 255, 0.04)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            },
                        }}
                    >
                        <Close sx={{ fontSize: '0.95rem' }} />
                    </IconButton>

                    <Typography
                        sx={{
                            fontSize: { xs: '1.25rem', xl: '1.4rem' },
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            color: colors.text.primary,
                        }}
                    >
                        {title}
                    </Typography>
                    {message && (
                        <Typography
                            sx={{
                                mt: '0.35rem',
                                maxWidth: '18rem',
                                fontSize: { xs: '0.88rem', xl: '0.95rem' },
                                lineHeight: 1.5,
                                color: colors.text.secondary,
                            }}
                        >
                            {message}
                        </Typography>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.75rem',
                            mt: '1.55rem',
                            width: '100%',
                            maxWidth: '22rem',
                            [MOBILE_MQ]: {
                                flexDirection: 'column',
                                maxWidth: '100%',
                            },
                        }}
                    >
                        <Button
                            onClick={onCancel}
                            sx={{
                                ...DIALOG_CANCEL_BUTTON_SX,
                                flex: { xs: 'none', sm: 1 },
                                minWidth: { sm: '9.5rem' },
                                [MOBILE_MQ]: {
                                    order: 0,
                                    width: '100%',
                                    minWidth: 0,
                                    height: "2.35rem",
                                    borderRadius: "6px",
                                },
                            }}
                        >
                            {cancelText}
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={onConfirm}
                            sx={{
                                ...LOBBY_EXIT_BUTTON_SX,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flex: { xs: 'none', sm: 1 },
                                minWidth: { sm: '9.5rem' },
                                [MOBILE_MQ]: {
                                    order: 1,
                                    width: '100%',
                                    minWidth: 0,
                                    height: "2.35rem",
                                    borderRadius: "6px",
                                },
                            }}
                        >
                            {confirmText}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </BackdropOverlay>
    );
}
